import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import {
  Phone, ArrowRight, Clock, Droplets, Flame, Home,
  ShieldCheck, Wrench, Zap, Sparkles, MapPin, Award, CheckCircle, Gauge
} from 'lucide-react';
import Hero from '../components/Hero';
import ServiceCards from '../components/ServiceCards';
import OwnerSection from '../components/OwnerSection';
import Testimonials from '../components/Testimonials';
import ServiceArea from '../components/ServiceArea';

const PHONE = "403-615-8770";
const EMAIL = "Radinovicw@shaw.ca";

// -----------------------------------------------------
// Quick info strip directly under hero (compact)
// -----------------------------------------------------
const InfoStrip = () => {
  const items = [
    { icon: Award, label: '25+ Yrs Trade Experience' },
    { icon: ShieldCheck, label: 'Licensed & Insured' },
    { icon: MapPin, label: 'Calgary & Surrounding' },
    { icon: Clock, label: 'Open 7 Days · 24 Hr Emergency' },
  ];
  return (
    <section className="relative pt-24 pb-10 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {items.map((it) => (
            <div key={it.label} className="flex items-center gap-3 px-4 py-3.5 bg-white border border-slate-200 rounded-lg">
              <span className="w-9 h-9 rounded-md bg-[#0F172A] flex items-center justify-center text-white flex-shrink-0">
                <it.icon className="w-4 h-4" strokeWidth={2} />
              </span>
              <span className="text-xs sm:text-sm font-semibold text-[#0F172A] leading-tight">{it.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// -----------------------------------------------------
// Common Problems — pill grid with mini-droplet icons
// -----------------------------------------------------
const CommonProblems = () => {
  const problems = [
    { icon: Flame, title: 'Water Heater Issues', link: '/plumbing/water-heater-replacement-calgary', desc: 'Repair, replace, or upgrade to tankless' },
    { icon: Droplets, title: 'Poly-B Replacement', link: '/plumbing/poly-b-replacement-calgary', desc: 'Switch old Poly-B to PEX or copper' },
    { icon: Home, title: 'Suite Plumbing', link: '/plumbing/secondary-suite-plumbing-calgary', desc: 'Legal secondary suite rough-ins' },
    { icon: ShieldCheck, title: 'Backflow Testing', link: '/plumbing/backflow-testing-calgary', desc: 'City of Calgary certified' },
    { icon: Wrench, title: 'In-Floor Heating', link: '/heating/in-floor-radiant-heating-calgary', desc: 'Hydronic radiant floor systems' },
    { icon: Flame, title: 'Boiler & Hydronic', link: '/heating/boiler-hydronic-calgary', desc: 'Service, repair & install' },
    { icon: Gauge, title: 'BBQ Gas Lines', link: '/gas-fitting/bbq-gas-lines-calgary', desc: 'Safe, code-compliant lines' },
    { icon: Flame, title: 'Furnace Repair', link: '/heating/furnace-installation-repair-calgary', desc: 'Reliable furnace service' },
  ];

  return (
    <section data-testid="common-problems-section" className="py-24 bg-white relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-14 max-w-2xl mx-auto"
        >
          <span className="eyebrow mb-5">How We Help</span>
          <h2 className="font-heading font-extrabold text-4xl sm:text-5xl text-[#0F172A] mt-5 mb-4 leading-tight">
            Common projects <span className="text-[#0284C7]">we handle every week.</span>
          </h2>
          <p className="text-slate-600 text-lg">
            From quick repairs to planned upgrades and renovations &mdash; DNB takes care of the work end-to-end.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {problems.map((problem, index) => (
            <motion.div
              key={problem.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
            >
              <Link to={problem.link} className="dnb-card block h-full group">
                <div className="droplet-icon droplet-icon-sm droplet-icon-light mb-4">
                  <problem.icon className="w-5 h-5" strokeWidth={2} />
                </div>
                <h3 className="font-heading font-bold text-lg text-[#0F172A] mb-1 group-hover:text-[#0284C7] transition-colors leading-tight">
                  {problem.title}
                </h3>
                <p className="text-slate-500 text-sm">{problem.desc}</p>
                <div className="mt-4 inline-flex items-center gap-1.5 text-[#0284C7] font-bold text-xs uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity">
                  Learn more
                  <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// -----------------------------------------------------
// Process Section — "How DNB works" — new horizontal step layout
// -----------------------------------------------------
const ProcessSection = () => {
  const steps = [
    { num: '01', title: 'Get in touch', text: 'Call Mike directly or send a quick request. We respond fast with clear next steps.' },
    { num: '02', title: 'On-site assessment', text: 'We come out, review the work, and give you a straight, no-pressure estimate.' },
    { num: '03', title: 'Clean work, done right', text: 'Quality install, tidy site, no shortcuts. We finish properly and walk you through it.' },
  ];

  return (
    <section data-testid="process-section" className="py-24 section-deep-gradient relative overflow-hidden">
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-2xl mx-auto mb-14"
        >
          <span className="eyebrow eyebrow-light mb-5">Our Process</span>
          <h2 className="font-heading font-extrabold text-4xl sm:text-5xl text-white mt-5 mb-4 leading-tight">
            Simple. Honest. <span className="text-[#0284C7]">Hands-on.</span>
          </h2>
          <p className="text-slate-300/80 text-lg">
            No pushy sales calls, no over-quoting. Just trade work done properly.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 relative">
          {/* Connecting line */}
          <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-px bg-gradient-to-r from-transparent via-sky-400/40 to-transparent"></div>

          {steps.map((s, i) => (
            <motion.div
              key={s.num}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="dnb-card-glass text-center"
            >
              <div className="droplet-icon mx-auto mb-5">
                <span className="font-heading font-extrabold text-base">{s.num}</span>
              </div>
              <h3 className="font-heading font-bold text-xl text-white mb-2">{s.title}</h3>
              <p className="text-slate-300/80 text-sm leading-relaxed">{s.text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// -----------------------------------------------------
// Why DNB — stat row
// -----------------------------------------------------
const WhyDNB = () => {
  const reasons = [
    { icon: ShieldCheck, title: 'Licensed & Insured', desc: 'Journeyman plumber & gas fitter. Full liability coverage. WCB-protected.' },
    { icon: Sparkles, title: 'Clean Workmanship', desc: 'Tidy job sites, neat installs, respect for your home or property.' },
    { icon: Zap, title: 'Practical Solutions', desc: 'Honest recommendations — we don\'t over-sell what you don\'t need.' },
    { icon: Home, title: 'Owner-Operated', desc: 'You deal directly with Mike, never a call centre or middleman.' },
  ];

  return (
    <section data-testid="why-choose-section" className="py-24 bg-white relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-2xl mx-auto mb-14"
        >
          <span className="eyebrow mb-5">Why DNB</span>
          <h2 className="font-heading font-extrabold text-4xl sm:text-5xl text-[#0F172A] mt-5 mb-4 leading-tight">
            A different kind of <br className="hidden sm:block" />
            <span className="text-[#0284C7]">Calgary contractor.</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {reasons.map((reason, index) => (
            <motion.div
              key={reason.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.08 }}
              className="dnb-card text-center"
            >
              <div className="droplet-icon mx-auto mb-5">
                <reason.icon className="w-6 h-6" strokeWidth={2} />
              </div>
              <h3 className="font-heading font-bold text-lg text-[#0F172A] mb-2">{reason.title}</h3>
              <p className="text-slate-600 text-sm leading-relaxed">{reason.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// -----------------------------------------------------
// Electrical Partner — refined card with droplet treatment
// -----------------------------------------------------
const ElectricalPartner = () => {
  return (
    <section data-testid="electrical-partner-preview" className="py-24 section-tint">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative bg-white rounded-lg p-10 lg:p-16 border border-slate-200 shadow-md overflow-hidden"
        >
          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <span className="eyebrow mb-5">Trusted Partner</span>
              <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-[#0F172A] mt-5 mb-4 leading-tight">
                For electrical, we trust{' '}
                <span className="text-[#0284C7]">Brandon at YYC Electrical Solutions.</span>
              </h2>
              <p className="text-slate-600 mb-3 leading-relaxed">
                Plumbing, heating, and gas fitting work often involves electrical &mdash; new water heater wiring, boiler controls, garage heater circuits, thermostat hookups, dedicated appliance lines.
              </p>
              <p className="text-slate-600 mb-6 leading-relaxed">
                DNB partners with <strong className="text-[#0F172A]">Brandon, owner of YYC Electrical Solutions</strong>, who brings the same standards we do &mdash; clean work, fair pricing, reliable service.
              </p>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 text-[#0284C7] font-bold hover:gap-3 transition-all"
              >
                Read about our partnership
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="lg:col-span-5 flex justify-center">
              <div className="text-center w-full max-w-xs">
                <div className="droplet-icon droplet-icon-lg mx-auto mb-5" style={{background: '#F59E0B'}}>
                  <Zap className="w-10 h-10" strokeWidth={2} />
                </div>
                <p className="text-[#0F172A] font-heading font-extrabold text-xl">YYC Electrical Solutions</p>
                <p className="text-slate-600 font-semibold text-sm mt-1">Master Electrician &middot; Brandon</p>
                <div className="mt-4 flex flex-wrap gap-1.5 justify-center">
                  {['Licensed', 'Local', 'Trusted by DNB'].map((tag) => (
                    <span key={tag} className="px-2.5 py-1 bg-slate-100 text-[#0F172A] text-xs font-semibold rounded-full border border-slate-200">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// -----------------------------------------------------
// Final CTA section — bold dark with floating droplets
// -----------------------------------------------------
const FinalCTA = () => {
  return (
    <section data-testid="final-cta" className="py-24 cta-banner relative">
      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <span className="eyebrow eyebrow-light mb-6">Let's Get Started</span>
        <h2 className="font-heading font-extrabold text-4xl md:text-5xl text-white mt-5 mb-5 leading-tight">
          Ready to book <br className="hidden sm:block" />
          <span className="text-[#0284C7]">your service?</span>
        </h2>
        <p className="text-slate-300/90 mb-10 max-w-xl mx-auto text-lg">
          Get in touch with Mike directly for fast, reliable plumbing, heating, and gas fitting work in Calgary and surrounding areas.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/contact"
            className="btn-primary inline-flex items-center justify-center gap-2"
          >
            Request Free Estimate
            <ArrowRight className="w-4 h-4" />
          </Link>
          <a
            href={`tel:${PHONE}`}
            className="btn-ghost-light inline-flex items-center justify-center gap-2"
          >
            <Phone className="w-4 h-4" />
            Call {PHONE}
          </a>
        </div>
        <p className="mt-8 text-slate-300/70 text-sm">
          Or email anytime: <a href={`mailto:${EMAIL}`} className="underline hover:text-white">{EMAIL}</a>
        </p>
      </div>
    </section>
  );
};

// -----------------------------------------------------
// Page assembly
// -----------------------------------------------------
export const HomePage = () => {
  return (
    <>
      <Helmet>
        <title>DNB Plumbing, Heating & Gas Fitting Inc. | Calgary Plumber Since 2009</title>
        <meta name="description" content="DNB Plumbing, Heating & Gas Fitting Inc.. Owner-operated by Mike Radinovic — journeyman plumber & gas fitter with 25+ years of experience. Serving Calgary since 2009. Call 403-615-8770." />
        <link rel="canonical" href="/" />
      </Helmet>
      <main data-testid="home-page" className="page-transition">
        <Hero />
        <InfoStrip />
        <ServiceCards />
        <OwnerSection />
        <CommonProblems />
        <ProcessSection />
        <WhyDNB />
        <Testimonials />
        <ElectricalPartner />
        <ServiceArea />
        <FinalCTA />
      </main>
    </>
  );
};

export default HomePage;
